/**
 * Offline demo media (`?fake=1`).
 *
 * The real media path needs the control plane (token) and a LiveKit edge. Hosts without the Docker
 * stack still need a runnable demo, so this module builds a `FakeTransport` from `@nimeet/rtc-web`
 * (the same deterministic transport the SDK's own tests use) and drives it with scenario-shaped
 * participants: peers join, publish tracks, speak, comment and raise hands on the real data topics.
 *
 * Nothing here is presented as real media: the UI shows an "offline demo" tag and the docs label
 * every offline screenshot `-offline`. Remote "video" is a locally generated canvas track so the
 * demo still renders moving frames without a camera on the other side.
 */
import {
  createClient,
  createFakeTransport,
  type FakeTransport,
  type RtcClient,
} from '@nimeet/rtc-web';
import type { MeetingRole } from '../meeting/session';
import type { SceneId } from './scenes';

export interface DemoPeer {
  uid: number;
  name: string;
  role: MeetingRole;
  video: boolean;
  /** Milliseconds after join before this peer appears (lets the 1v1 "waiting" state render). */
  delayMs: number;
}

const PEERS: Record<SceneId, (localRole: MeetingRole) => DemoPeer[]> = {
  meeting: () => [
    { uid: 20002, name: 'Grace Hopper', role: 'host', video: true, delayMs: 700 },
    { uid: 20003, name: 'Linus Torvalds', role: 'audience', video: true, delayMs: 1600 },
  ],
  '1v1': () => [{ uid: 20002, name: 'Grace Hopper', role: 'host', video: true, delayMs: 2600 }],
  live: (localRole) =>
    localRole === 'host'
      ? [
          { uid: 20002, name: 'Grace Hopper', role: 'audience', video: false, delayMs: 600 },
          { uid: 20003, name: 'Linus Torvalds', role: 'audience', video: false, delayMs: 1300 },
        ]
      : [{ uid: 20002, name: 'Grace Hopper', role: 'host', video: true, delayMs: 600 }],
  voice: () => [
    { uid: 20002, name: 'Grace Hopper', role: 'host', video: false, delayMs: 500 },
    { uid: 20003, name: 'Linus Torvalds', role: 'audience', video: false, delayMs: 1100 },
    { uid: 20004, name: 'Barbara Liskov', role: 'audience', video: false, delayMs: 1700 },
  ],
};

const CHAT_SEEDS: Record<
  SceneId,
  { topic: string; payload: (peer: DemoPeer) => Record<string, unknown> } | null
> = {
  meeting: {
    topic: 'chat',
    payload: (peer) => ({
      type: 'chat',
      id: `demo_chat_${peer.uid}`,
      from: peer.uid,
      fromName: peer.name,
      text: 'Hi everyone - the offline demo is running in this tab.',
      at: new Date().toISOString(),
      kind: 'user',
    }),
  },
  '1v1': null,
  live: {
    topic: 'live',
    payload: (peer) => ({
      type: 'live:comment',
      id: `demo_live_${peer.uid}`,
      from: peer.uid,
      fromName: peer.name,
      text: 'Great stream! (offline demo comment)',
      at: new Date().toISOString(),
      kind: 'user',
    }),
  },
  voice: null,
};

/** Draw a fake participant camera: gradient + initials + a moving level meter. */
function createSyntheticVideoTrack(
  name: string,
  seed: number,
): { track: MediaStreamTrack; dispose: () => void } | null {
  if (typeof document === 'undefined' || typeof HTMLCanvasElement === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 360;
  const context = canvas.getContext('2d');
  if (!context || typeof canvas.captureStream !== 'function') return null;
  const hue = (seed * 47) % 360;
  let frame = 0;
  const draw = (): void => {
    frame += 1;
    const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, `hsl(${hue}, 55%, 46%)`);
    gradient.addColorStop(1, `hsl(${(hue + 40) % 360}, 60%, 24%)`);
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = 'rgba(255,255,255,0.92)';
    context.font = '600 44px ui-sans-serif, system-ui, sans-serif';
    context.textAlign = 'center';
    context.fillText(name, canvas.width / 2, canvas.height / 2 - 4);
    context.font = '400 22px ui-sans-serif, system-ui, sans-serif';
    context.fillStyle = 'rgba(255,255,255,0.72)';
    context.fillText('offline demo participant', canvas.width / 2, canvas.height / 2 + 34);
    for (let index = 0; index < 7; index += 1) {
      const level = 12 + Math.abs(Math.sin((frame + index) / 4)) * 46;
      context.fillStyle = `rgba(255,255,255,${0.32 + index * 0.05})`;
      context.fillRect(canvas.width / 2 - 84 + index * 26, canvas.height - 44 - level, 14, level);
    }
  };
  draw();
  const stream = canvas.captureStream(8);
  const track = stream.getVideoTracks()[0];
  if (!track) return null;
  const timer = window.setInterval(draw, 220);
  return {
    track,
    dispose: () => {
      window.clearInterval(timer);
      track.stop();
    },
  };
}

export interface OfflineDemoOptions {
  scene: SceneId;
  localRole: MeetingRole;
  localUid: number;
  localName: string;
}

/** Owns the fake transport + the scenario simulation timers. */
export class OfflineDemo {
  readonly client: RtcClient;
  readonly transport: FakeTransport;
  private readonly timers: number[] = [];
  private readonly disposers: Array<() => void> = [];
  private started = false;

  constructor(private readonly options: OfflineDemoOptions) {
    this.transport = createFakeTransport();
    this.client = createClient({
      appId: 'demo-app-id',
      tokenProvider: {
        type: 'callback',
        getRTCTokenInfo: async (request) => ({
          appId: 'demo-app-id',
          rtcToken: `offline.${btoa(JSON.stringify({ ch: request.channelName, uid: request.uid ?? options.localUid }))}.demo`,
          rtcUid: request.uid ?? options.localUid,
        }),
      },
      transport: this.transport,
      autoRenew: false,
      logLevel: 'warn',
      statsIntervalMs: 3000,
    });
  }

  /** Called after `MeetingSession.join` resolved: peers, speaking, comments and stats start now. */
  start(): void {
    if (this.started) return;
    this.started = true;
    const { scene, localRole } = this.options;
    const peers = PEERS[scene](localRole);

    // Local stats so the header shows something plausible in the offline demo.
    this.transport.setStats({ rttMs: 32, bitrateKbps: 1180, videoFreezeMs: 0, audioFreezeMs: 0 });

    for (const peer of peers) {
      const timer = window.setTimeout(() => {
        this.transport.simulateUserJoined({
          uid: peer.uid,
          userId: `demo-${peer.uid}`,
          role: peer.role,
        });
        // Peers announce their app-level role exactly like the online path does.
        this.transport.simulateData(
          peer.uid,
          JSON.stringify({
            type: 'presence',
            uid: peer.uid,
            displayName: peer.name,
            role: peer.role,
          }),
          'meeting',
        );
        this.transport.simulateTrackPublished(peer.uid, 'audio');
        if (peer.video) {
          this.transport.simulateTrackPublished(peer.uid, 'video');
          const synthetic = createSyntheticVideoTrack(peer.name, peer.uid);
          if (synthetic) {
            this.transport.setRemoteMedia(peer.uid, 'video', synthetic.track);
            this.disposers.push(synthetic.dispose);
          }
        }
        this.transport.setStats(
          { rttMs: 28 + (peer.uid % 40), bitrateKbps: 900 + (peer.uid % 300) },
          peer.uid,
        );
      }, peer.delayMs);
      this.timers.push(timer);
    }

    // Speaking levels: voice rooms rely on them, meetings show the speaking ring too.
    const speakerTimer = window.setInterval(() => {
      peers.forEach((peer, index) => {
        const level = (Date.now() / 900 + index) % 2 > 1 ? 0.18 : 0.01;
        this.transport.simulateVolume(peer.uid, level);
      });
    }, 900);
    this.timers.push(speakerTimer);

    const seed = CHAT_SEEDS[scene];
    const firstPeer = peers[0];
    if (seed && firstPeer) {
      const timer = window.setTimeout(() => {
        this.transport.simulateData(
          firstPeer.uid,
          JSON.stringify(seed.payload(firstPeer)),
          seed.topic,
        );
      }, 2400);
      this.timers.push(timer);
    }

    // A live host receiving reactions/comments from the audience would be one browser each; the
    // offline demo seeds one reaction so the counters are exercised.
    if (scene === 'live') {
      const timer = window.setTimeout(() => {
        const audience = peers.find((peer) => peer.role === 'audience');
        if (audience) {
          this.transport.simulateData(
            audience.uid,
            JSON.stringify({ type: 'live:reaction', kind: 'clap', uid: audience.uid }),
            'live',
          );
        }
      }, 3600);
      this.timers.push(timer);
    }
  }

  dispose(): void {
    this.started = false;
    for (const timer of this.timers.splice(0)) {
      window.clearTimeout(timer);
      window.clearInterval(timer);
    }
    for (const dispose of this.disposers.splice(0)) dispose();
    void this.client.dispose().catch(() => undefined);
  }
}
